import { useState } from "react";
import LogoMark from "../components/Logo";
import { useNavigate } from "react-router-dom";
import { login, getMe } from "../api/auth";
import { useAuth } from "../lib/auth";
import Button from "../components/Button";
import { ApiError } from "../lib/api";

export default function LoginPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const token = await login(email, password);
      localStorage.setItem("trade1.token", token.access_token);
      const me = await getMe();
      setSession(token.access_token, {
        id: me.id,
        email: me.email,
        is_admin: me.is_admin,
        display_name: me.display_name,
      });
      navigate("/dashboard");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status === 401
            ? "Invalid email or password"
            : `HTTP ${e.status}`
          : "Network error";
      setError(msg);
      localStorage.removeItem("trade1.token");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas text-fg">
      <div className="w-full max-w-md bg-surface shadow-card rounded-lg p-8 border border-border">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark size={38} className="shrink-0 text-fg" />
          <div>
            <h1 className="text-xl font-bold tracking-tight leading-none">
              <span className="text-fg">Backtest</span>
              <span className="font-light text-muted"> Club</span>
            </h1>
            <p className="text-sm text-muted mt-1">
              Backtest, deploy and trade — sign in with your platform credentials.
            </p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label="Email"
            input={
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-surface border border-border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            }
          />
          <Field
            label="Password"
            input={
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface border border-border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            }
          />
          {error && (
            <div className="text-sm text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border border-down-200 dark:border-down-400/30 rounded px-3 py-2">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full" size="lg">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, input }: { label: string; input: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-fg mb-1">{label}</label>
      {input}
    </div>
  );
}
